import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { AxiosResponse } from 'axios';
import { put, takeLatest } from 'redux-saga/effects';
import {
  deleteMapping,
  deleteMappingFailure,
  deleteMappingSuccess,
  getBucketPreferences,
  getBucketPreferencesFailure,
  getBucketPreferencesSuccess,
  getColumnMappingById,
  getColumnMappingByIdFailure,
  getColumnMappingByIdSuccess,
  getCustomBucketList,
  getCustomBucketListSuccess,
  getCustomProductList,
  getCustomProductListSuccess,
  getDBColumns,
  getDBColumnsFailure,
  getDBColumnsSuccess,
  getFilteredMappings,
  getFilteredMappingsFailure,
  getFilteredMappingsSuccess,
  getLOBMapping,
  getLOBMappingFailure,
  getLOBMappingSuccess,
  getProductPreferences,
  getProductPreferencesFailure,
  getProductPreferencesSuccess,
  mapAllocatorToAgency,
  mapAllocatorToAgencyFailure,
  mapAllocatorToAgencySuccess,
  updateColumnMapping,
  updateColumnMappingFailure,
  updateColumnMappingSuccess,
  uploadColumnMapping,
  uploadColumnMappingFailure,
  uploadColumnMappingSuccess,
} from 'redux/slice/allocation-mapping/AllocationMapping.slice';
import {
  IGetBucketPreferencesResponse,
  IGetColumnMappingRequest,
  IGetColumnMappingResponse,
  IGetDBColumnsResponse,
  IGetFilteredMappingRequest,
  IGetFilteredMappingResponse,
  IGetProductBucketPreferencesRequest,
  IGetProductPreferencesResponse,
  IMapAllocatorToAgencyRequest,
  IMapAllocatorToAgencyResponse,
  IMappedLOBsRequest,
  IMappedLOBsResponse,
  IUpdateColumnMappingRequest,
  IUpdateColumnMappingResponse,
  IUploadColumnMappingRequest,
  IUploadColumnMappingResponse,
} from 'redux/types/AllocationMapping.types';
import { columnMappingEndPoints } from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';

import { handleError } from '../utils/errorUtils';

// Saga for auto assigning agents
function* uploadColumnMappingSaga(
  action: PayloadAction<IUploadColumnMappingRequest>
) {
  try {
    const { mappingFile, buckets, products, fileType } = action.payload;

    const formdata = new FormData();
    formdata.append('mappingFile', mappingFile);
    formdata.append('product', JSON.stringify(products));
    formdata.append('bucket', JSON.stringify(buckets));
    formdata.append('fileType', fileType);

    const response: AxiosResponse<IUploadColumnMappingResponse> =
      yield HTTPClient.PostRequest(
        `${columnMappingEndPoints.UPLOAD_COLUMN_MAPPING()}`,
        formdata,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
    yield put(uploadColumnMappingSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, uploadColumnMappingFailure, '');
  }
}

function* getMappingSaga(action: PayloadAction<IMappedLOBsRequest>) {
  try {
    const { fileType } = action.payload;
    const response: AxiosResponse<IMappedLOBsResponse> =
      yield HTTPClient.GetRequest(
        `${columnMappingEndPoints.GET_MAPPING(fileType)}`
      );
    yield put(getLOBMappingSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, getLOBMappingFailure, '');
  }
}

function* getColumnMappingByIdSaga(
  action: PayloadAction<IGetColumnMappingRequest>
) {
  try {
    const { id } = action.payload;
    const response: AxiosResponse<IGetColumnMappingResponse> =
      yield HTTPClient.GetRequest(
        `${columnMappingEndPoints.GET_MAPPING_BY_ID(id)}`
      );
    yield put(getColumnMappingByIdSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, getColumnMappingByIdFailure, '');
  }
}

function* deleteMappingSaga(
  action: PayloadAction<{ id: string; fileType: string }>
) {
  try {
    const { id, fileType } = action.payload;
    const response: AxiosResponse<IUpdateColumnMappingResponse> =
      yield HTTPClient.DeleteRequest(
        `${columnMappingEndPoints.DELETE_MAPPING(id)}`
      );
    yield put(deleteMappingSuccess(response.data));

    // Call get all mappings
    const getLOBMappingAction = {
      type: getLOBMapping.type,
      payload: {
        fileType,
      },
    };
    yield put(getLOBMappingAction); // Dispatch the action

    message.success(response.data.message);
  } catch (error: any) {
    yield* handleError(error, deleteMappingFailure, '');
  }
}
function* updateColumnMappingSaga(
  action: PayloadAction<IUpdateColumnMappingRequest>
) {
  try {
    const { id, columnMapping } = action.payload;
    const response: AxiosResponse<IUpdateColumnMappingResponse> =
      yield HTTPClient.PutRequest(
        `${columnMappingEndPoints.UPDATE_MAPPING(id)}`,
        {
          columnMapping,
        }
      );
    yield put(updateColumnMappingSuccess(response.data));
    message.success(response.data.message);

    // Call get column mapping
    const getColumnMappingByIdAction = {
      type: getColumnMappingById.type,
      payload: {
        id,
      },
    };
    yield put(getColumnMappingByIdAction); // Dispatch the action
  } catch (error: any) {
    yield* handleError(error, updateColumnMappingFailure, '');
  }
}
function* getFilteredMappingsSaga(
  action: PayloadAction<IGetFilteredMappingRequest>
) {
  try {
    const { allocatorId, bucket, fileType, product } = action.payload;
    const response: AxiosResponse<IGetFilteredMappingResponse> =
      yield HTTPClient.PutRequest(
        `${columnMappingEndPoints.GET_COLUMN_MAPPING_BY_FILTER()}`,
        {
          allocatorId,
          bucket,
          fileType,
          product,
        }
      );
    yield put(getFilteredMappingsSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, getFilteredMappingsFailure, '');
  }
}
function* mapAllocatorToAgencySaga(
  action: PayloadAction<IMapAllocatorToAgencyRequest>
) {
  try {
    const { agencyId, mappingId } = action.payload;
    const response: AxiosResponse<IMapAllocatorToAgencyResponse> =
      yield HTTPClient.PutRequest(
        `${columnMappingEndPoints.MAP_ALLOCATOR_TO_AGENCY()}`,
        {
          agencyId,
          mappingId,
        }
      );
    yield put(mapAllocatorToAgencySuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, mapAllocatorToAgencyFailure, '');
  }
}
function* getProductPreferencesSaga(
  action: PayloadAction<IGetProductBucketPreferencesRequest>
) {
  try {
    const { query, enabled, offset = 0, limit = 10 } = action.payload;
    const response: AxiosResponse<IGetProductPreferencesResponse> =
      yield HTTPClient.GetRequest(
        `${columnMappingEndPoints.GET_PRODUCT_PREFERENCES(
          query,
          enabled,
          offset,
          limit
        )}`
      );
    yield put(getProductPreferencesSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, getProductPreferencesFailure, '');
  }
}
function* getBucketPreferencesSaga(
  action: PayloadAction<IGetProductBucketPreferencesRequest>
) {
  try {
    const { query, enabled, offset = 0, limit = 10 } = action.payload;
    const response: AxiosResponse<IGetBucketPreferencesResponse> =
      yield HTTPClient.GetRequest(
        `${columnMappingEndPoints.GET_BUCKET_PREFERENCES(
          query,
          enabled,
          offset,
          limit
        )}`
      );
    yield put(getBucketPreferencesSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, getBucketPreferencesFailure, '');
  }
}
function* getDBColumnsSaga() {
  try {
    const response: AxiosResponse<IGetDBColumnsResponse> =
      yield HTTPClient.GetRequest(`${columnMappingEndPoints.GET_DB_COLUMNS()}`);
    yield put(getDBColumnsSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, getDBColumnsFailure, '');
  }
}

function* getCustomProductListSaga(action: PayloadAction<string[]>) {
  try {
    const allocatorIds = action.payload;
    const response: AxiosResponse<IGetProductPreferencesResponse> =
      yield HTTPClient.PostRequest(
        `${columnMappingEndPoints.GET_CUSTOM_PRODUCT_LIST}`,
        allocatorIds
      );
    yield put(getCustomProductListSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, getProductPreferencesFailure, '');
  }
}
function* getCustomBucketListSaga(action: PayloadAction<string[]>) {
  try {
    const allocatorIds = action.payload;
    const response: AxiosResponse<IGetBucketPreferencesResponse> =
      yield HTTPClient.PostRequest(
        `${columnMappingEndPoints.GET_CUSTOM_BUCKET_LIST}`,
        allocatorIds
      );
    yield put(getCustomBucketListSuccess(response.data));
  } catch (error: any) {
    yield* handleError(error, getBucketPreferencesFailure, '');
  }
}

export function* watchAllocationMappingSagas() {
  yield takeLatest(uploadColumnMapping.type, uploadColumnMappingSaga);
  yield takeLatest(getLOBMapping.type, getMappingSaga);
  yield takeLatest(getColumnMappingById.type, getColumnMappingByIdSaga);
  yield takeLatest(deleteMapping.type, deleteMappingSaga);
  yield takeLatest(updateColumnMapping.type, updateColumnMappingSaga);
  yield takeLatest(getFilteredMappings.type, getFilteredMappingsSaga);
  yield takeLatest(mapAllocatorToAgency.type, mapAllocatorToAgencySaga);
  yield takeLatest(getProductPreferences.type, getProductPreferencesSaga);
  yield takeLatest(getBucketPreferences.type, getBucketPreferencesSaga);
  yield takeLatest(getCustomProductList.type, getCustomProductListSaga);
  yield takeLatest(getCustomBucketList.type, getCustomBucketListSaga);

  yield takeLatest(getDBColumns.type, getDBColumnsSaga);
}
